import { BorderStyle, Box, useToggle } from '@factorialco/deprecated-design-system'
import type moment from 'moment'

import { observer } from 'mobx-react'

import { dateToString } from 'modules/core/lib/dates'

import AddShiftContextualButton from 'modules/shift_management/components/ShiftManagement/AddShiftContextualButton'
import EmptyShiftCell from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/EmptyShiftCell'
import useCopyShift from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/useCopyShift'
import useMoveShift from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/useMoveShift'
import useDateRangeSelection from 'modules/shift_management/hooks/useDateRangeSelection'
import type { DragItem } from 'modules/shift_management/hooks/useDragAndDrop'
import { useDropShift } from 'modules/shift_management/hooks/useDragAndDrop'
import { usePreview } from 'modules/shift_management/hooks/usePreview'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import {
  useSelectionDispatch,
  useSelectionState,
} from 'modules/shift_management/hooks/useSelection'
import type DefaultShifts from 'modules/shift_management/store/internal/collections/DefaultShifts'
import type ShiftsRow from 'modules/shift_management/store/internal/models/ShiftsRow'

import DropItem from './DropItem'

interface EmptyCellActionsProps {
  shiftsRow: ShiftsRow
  date: moment.Moment
  isOver: boolean
  isSelected?: boolean
  visibleDefaultShifts: DefaultShifts
}

const EmptyCellActions = ({
  shiftsRow,
  date,
  isOver,
  isSelected = false,
  visibleDefaultShifts,
}: EmptyCellActionsProps) => {
  const [isHovering, toggleHovering] = useToggle(false)
  const dispatchSelection = useSelectionDispatch()
  const { showEmptyShiftCell } = useSelectionState()
  const [{ view }] = useSchedule()
  const { moveShift } = useMoveShift()
  const { copyShift } = useCopyShift()
  const [{ shifts: previewShifts }] = usePreview()
  const hasPreview = !!previewShifts.length
  // Hide the selected empty cells when there is a preview
  const isHidden = isSelected && hasPreview

  const formattedDate = dateToString(date, 'backend')
  const employeeIds = shiftsRow.employeeIds

  // This is the current row id, which equals to the employee id.
  // We use this inside useDropShift but we need to calculate before to
  // not shadow the drop item which also includes a shiftsRow
  const shiftsRowsId = shiftsRow.id

  const daysByEmployee = useDateRangeSelection(
    false,
    employeeIds,
    formattedDate,
    formattedDate,
    '',
    true
  )

  const {
    isOverCurrent: isOverMoveOption,
    drop: dropOverMoveShift,
    originalItem,
  } = useDropShift({
    shiftsRow,
    date,
    action: async (item: DragItem) => {
      await moveShift({
        newShiftsRow: shiftsRow,
        shift: item.shift,
        unassignedShift: item.unassignedShift,
        date,
        daysByEmployee,
        employeeId: view.isByAreas && item.shift ? item.shift.employeeId : shiftsRowsId,
      })
    },
  })

  const { isOverCurrent: isOverCopyOption, drop: dropOverCopyShift } = useDropShift({
    shiftsRow,
    date,
    action: async (item: DragItem) => {
      if (!item.shiftsRow || !item.shift) return

      await copyShift({
        shift: item.shift,
        date,
        employeeId: view.isByAreas ? item.shift?.employeeId : shiftsRow.id,
      })
    },
  })

  const onSelect = () => {
    dispatchSelection({
      type: 'TOGGLE_DAY',
      day: date,
      shiftsRow,
      view,
    })
  }

  const isHoveringWhileDragging = isOver || isOverMoveOption || isOverCopyOption
  const flexDirection = isOver ? 'row' : 'column'
  const justifyContent = isOver ? 'flexStart' : 'flexEnd'

  if (!shiftsRow.canManage) return null

  const height = view.isDaily ? 'full' : view.cellMinHeight

  const cellHeight = showEmptyShiftCell && isSelected ? undefined : height

  const background =
    isHovering && !isHoveringWhileDragging && !showEmptyShiftCell ? 'grey100' : 'grey000White'

  const border: BorderStyle = {
    style: 'solid',
    width: 's2',
    color: 'grey800',
  }

  const borderStyle = isSelected && !showEmptyShiftCell ? border : undefined

  return (
    <Box
      // Can't use maxWidth prop directly because gamma does not have s170 as a dimension size
      // which is necessary to accommodate the new cell design
      height={cellHeight}
      background={background}
      cursor="pointer"
      border={{ all: borderStyle }}
      borderRadius={{ all: 'abs008' }}
      onClick={onSelect}
      onMouseEnter={toggleHovering(true)}
      onMouseLeave={toggleHovering(false)}
      flexDirection={flexDirection}
      gap="s12"
      justifyContent={justifyContent}
      style={{ display: isHidden ? 'none' : 'flex' }}
    >
      {showEmptyShiftCell && isSelected && <EmptyShiftCell shiftsRow={shiftsRow} />}
      {isOver && (
        <>
          <DropItem ref={dropOverMoveShift} isOver={isOverMoveOption} icon="move-shifts" />
          {!view.isByAreas && view.isWeekly && !!originalItem.shift && (
            <DropItem ref={dropOverCopyShift} isOver={isOverCopyOption} icon="duplicate" />
          )}
        </>
      )}
      <AddShiftContextualButton
        date={date}
        shiftsRow={shiftsRow}
        visibleDefaultShifts={visibleDefaultShifts}
      />
    </Box>
  )
}

export default observer(EmptyCellActions)
